import PropTypes from "prop-types";
import React, { useState, useMemo } from "react";
import { Container, Card, CardBody } from "reactstrap";

// PrimeReact
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";

// Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// i18n
import { withTranslation } from "react-i18next";
import ActionEditLink from "components/buttons/btnDetail";

const Clients = props => {
  // Meta title
  document.title = "Admin | Clients";

  // -------------------- STATE --------------------
  const [globalFilter, setGlobalFilter] = useState("");
  const [clientType, setClientType] = useState(null);
  const [planType, setPlanType] = useState(null);
  const [startDate, setStartDate] = useState(null);

  // -------------------- SAMPLE DATA --------------------
  const clients = [
    {
      id: 1,
      brokerageName: "ABC Realty",
      city: "Toronto",
      assignedAgent: "John Smith",
      clientType: "agent",
      planType: "monthly",
      amount: 299,
      licenses: 5,
      startDate: "2024-01-01",
      endDate: "2024-12-31",
      lastAccess: "2024-12-20 10:45 AM",
    },
    {
      id: 2,
      brokerageName: "Prime Homes",
      city: "Mississauga",
      assignedAgent: "Sarah Lee",
      clientType: "office",
      planType: "annual",
      amount: 2499,
      licenses: 20,
      startDate: "2024-02-15",
      endDate: "2025-02-14",
      lastAccess: "2024-12-22 03:10 PM",
    },
  ];

  // -------------------- FILTERED DATA --------------------
  const filteredClients = useMemo(() => {
    return clients.filter(client => {
      const matchesSearch =
        !globalFilter ||
        client.brokerageName.toLowerCase().includes(globalFilter.toLowerCase()) ||
        client.city.toLowerCase().includes(globalFilter.toLowerCase()) ||
        client.assignedAgent.toLowerCase().includes(globalFilter.toLowerCase());

      const matchesType = !clientType || client.clientType === clientType;
      const matchesPlan = !planType || client.planType === planType;

      const matchesDate =
        !startDate || new Date(client.startDate) >= new Date(startDate);

      return matchesSearch && matchesType && matchesPlan && matchesDate;
    });
  }, [clients, globalFilter, clientType, planType, startDate]);

  // -------------------- ACTION --------------------
  const viewDetails = row => {
    console.log("View Client Details:", row);
    // navigate(`/clients/${row.id}`) OR open modal
  };

  // -------------------- UI --------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs
            title={props.t("Clients")}
            breadcrumbItem={props.t("Dashboard")}
          />

          <Card>
            <CardBody>
              {/* FILTER BAR */}
              <div className="d-flex flex-wrap gap-3 mb-4">
                <InputText
                  placeholder="Search..."
                  value={globalFilter}
                  onChange={e => setGlobalFilter(e.target.value)}
                />

                <Dropdown
                  value={clientType}
                  options={[
                    { label: "Agent", value: "agent" },
                    { label: "Office", value: "office" },
                  ]}
                  onChange={e => setClientType(e.value)}
                  placeholder="Client Type"
                  showClear
                />

                <Dropdown
                  value={planType}
                  options={[
                    { label: "Monthly", value: "monthly" },
                    { label: "Annual", value: "annual" },
                  ]}
                  onChange={e => setPlanType(e.value)}
                  placeholder="Plan"
                  showClear
                />

                <Calendar
                  value={startDate}
                  onChange={e => setStartDate(e.value)}
                  placeholder="Start Date"
                  showIcon
                />
              </div>

              {/* DATA TABLE */}
              <DataTable
                value={filteredClients}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                responsiveLayout="scroll"
                emptyMessage="No clients found"
              >
                <Column field="brokerageName" header="Brokerage Name" sortable />
                <Column field="city" header="City" sortable />
                <Column field="assignedAgent" header="Assigned Agent" />
                <Column
                  field="planType"
                  header="Plan"
                  body={row => row.planType.toUpperCase()}
                />
                <Column
                  field="amount"
                  header="Amount"
                  body={row => `CAD ${row.amount}`}
                />
                <Column field="licenses" header="# Licenses" />
                <Column field="startDate" header="Start Date" />
                <Column field="endDate" header="End Date" />
                <Column field="lastAccess" header="Last Access" />

                <Column
                  header="Details"
                  body={row => (
                    <ActionEditLink to={`/client/contact-info`} />
                  
                  )}
                />
              </DataTable>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

Clients.propTypes = {
  t: PropTypes.any,
};

export default withTranslation()(Clients);
